import {
  buildFolderTree,
  type FolderNode,
} from "@/action-dialog/dialogs/folderTree";
import { useWorkspaceFileRecords } from "@/api/fileRecords";
import { useFolders } from "@/api/folders";
import { useWorkspaces } from "@/api/workspaces";
import { CommandItem } from "@/components/ui/command";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { useObjectActionMenuClose } from "@/object-actions/renderers/ObjectActionMenuCloseContext";
import { buildCanvasFolderTree } from "@/object-actions/transfer/canvasFolderTree";
import type {
  ObjectActionDestinationSubmenu,
  TransferDestination,
} from "@/object-actions/types";
import type { Workspace } from "@cohesive/api-client";
import { ChevronRight, Folder, Layers, User } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Cascading workspace → folder destination picker for `move` /
 * `copy-to` actions. Two hosts share the same data + rules:
 *
 * - `DestinationDropdownMenuSub` — Radix `DropdownMenuSub` (rail menus, pane
 *   kebab, detail-card kebabs). Hover-intent and keyboard nav come from Radix.
 * - `DestinationCommandMenuSub` — the canvas command menu's non-portaled
 *   popover submenu (cmdk can't host Radix Subs), generalized from the
 *   download-formats submenu with an added hover-open delay.
 *
 * Interaction model (per issue #2258): a workspace/folder with no children is
 * a plain clickable row (commit); one with children is a submenu trigger that
 * is ALSO clickable — click/Enter commits to that container, hover/ArrowRight
 * drills in. No explicit "Copy here" rows: the parent row is the commit
 * affordance, like any native nested menu. Folder data loads per workspace as
 * soon as the workspace list shows, so rows know up front whether to render a
 * chevron.
 */

function useSortedWorkspaces(
  submenu?: ObjectActionDestinationSubmenu,
): Workspace[] {
  const { data: workspaces = [] } = useWorkspaces();
  // Team workspaces first, private last, alpha within each group — matches
  // the pickers this replaces (MoveFileRecordDialog, CopyToWorkspaceDialog).
  const restrict = submenu?.restrictToWorkspaceIds;
  return useMemo(() => {
    const eligible = restrict
      ? workspaces.filter((ws) => restrict.includes(ws.id))
      : workspaces;
    return [...eligible].sort((a, b) => {
      if (a.is_private === b.is_private) return a.name.localeCompare(b.name);
      return a.is_private ? 1 : -1;
    });
  }, [restrict, workspaces]);
}

function workspaceLabel(ws: Workspace): string {
  return ws.is_private ? "My Workspace" : ws.name;
}

/** The record's current container — a disabled no-op target for moves. */
function isCurrentLocation(
  submenu: ObjectActionDestinationSubmenu,
  workspaceId: string,
  folderPath: string,
): boolean {
  return (
    submenu.operation === "move" &&
    submenu.currentWorkspaceId === workspaceId &&
    (submenu.currentFolderPath ?? "") === folderPath
  );
}

/**
 * A destination the actor cannot write to. Rendered disabled rather than
 * dropped, so a workspace you can see but not write to reads as "not allowed"
 * instead of quietly missing. Undefined = the check has not resolved; treat
 * as writable so the menu is never empty while it settles.
 */
function isNotWritable(
  submenu: ObjectActionDestinationSubmenu,
  workspaceId: string,
): boolean {
  return submenu.writableWorkspaceIds
    ? !submenu.writableWorkspaceIds.has(workspaceId)
    : false;
}

/** A folder can't move into itself or its own subtree (same workspace only). */
function isForbiddenFolder(
  submenu: ObjectActionDestinationSubmenu,
  workspaceId: string,
  folderPath: string,
): boolean {
  // Canvas folders key by id, so the subtree is an explicit set rather than a
  // path prefix; it applies in every workspace the folder could land in.
  if (submenu.forbiddenFolderIds?.has(folderPath)) return true;
  if (submenu.currentWorkspaceId !== workspaceId) return false;
  return (
    submenu.forbiddenPathPrefixes?.some((prefix) =>
      folderPath.startsWith(prefix),
    ) ?? false
  );
}

/** Close the whole menu first, then commit — mirrors the action-item defer. */
function useCommitAndClose(submenu: ObjectActionDestinationSubmenu) {
  const closeMenu = useObjectActionMenuClose();
  return useCallback(
    (destination: TransferDestination) => {
      closeMenu();
      window.setTimeout(() => submenu.commit(destination), 0);
    },
    [closeMenu, submenu],
  );
}

function useWorkspaceFolderTree(
  workspaceId: string,
  submenu: ObjectActionDestinationSubmenu,
): {
  tree: FolderNode[];
  isLoading: boolean;
} {
  const canvasFolders = submenu.treeSource === "canvas-folders";
  // Both queries are declared unconditionally to keep hook order stable; the
  // one that isn't the active tree source is disabled, so it never fetches.
  const fileQuery = useWorkspaceFileRecords(workspaceId, {
    enabled: !canvasFolders,
  });
  const folderQuery = useFolders(workspaceId, { enabled: canvasFolders });

  const tree = useMemo(() => {
    if (canvasFolders) {
      return folderQuery.data ? buildCanvasFolderTree(folderQuery.data) : [];
    }
    return fileQuery.data ? buildFolderTree(fileQuery.data) : [];
  }, [canvasFolders, fileQuery.data, folderQuery.data]);

  return {
    tree,
    isLoading: canvasFolders ? folderQuery.isLoading : fileQuery.isLoading,
  };
}

/** Build the destination a folder row commits to, for either tree. */
function folderDestination(
  submenu: ObjectActionDestinationSubmenu,
  workspaceId: string,
  node: { path: string; name: string },
  label: string,
): TransferDestination {
  if (submenu.treeSource === "canvas-folders") {
    return { workspaceId, folderPath: "", folderId: node.path, label };
  }
  return { workspaceId, folderPath: node.path, label };
}

function EmptyWorkspacesRow() {
  return (
    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
      No workspaces available.
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Cascade direction                                                          */
/*                                                                            */
/* When a level collision-flips to the left of its trigger, deeper levels     */
/* must continue leftward instead of popping back over the menus they came    */
/* from. Radix hardcodes submenu side from the menu root's `dir`, so each     */
/* level measures its rendered `data-side` (kept current via a               */
/* MutationObserver — Popper positions asynchronously) and provides the       */
/* direction to its children via context.                                     */
/* -------------------------------------------------------------------------- */

type CascadeSide = "left" | "right";

const CascadeSideContext = createContext<CascadeSide>("right");

/** Observe a popper content element's rendered side ("left" | "right"). */
function useMeasuredSide(el: HTMLElement | null): CascadeSide {
  const [side, setSide] = useState<CascadeSide>("right");
  useLayoutEffect(() => {
    if (!el) return;
    const read = () => {
      const value = el.getAttribute("data-side");
      if (value === "left" || value === "right") setSide(value);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(el, { attributes: true, attributeFilter: ["data-side"] });
    return () => observer.disconnect();
  }, [el]);
  return side;
}

/**
 * The seam between a cascade level and its child panel — one constant for
 * both hosts, so the dropdown (rail/kebab) and command-menu (canvas) pickers
 * step identically.
 */
const CASCADE_GAP_PX = 4;

/**
 * Mirrored sideOffset that places a submenu to the LEFT of its trigger row.
 * Radix menu SubContent has no `side` prop (placement is fixed "right" for
 * ltr), so a leftward cascade is forced by shifting along the side axis:
 * default is child.left = row.right; mirrored is child.right = row.left.
 * Levels hug their content, so the child's true width is only known once its
 * content mounts — the parent's width serves as the first-frame estimate and
 * the offset corrects as soon as the child element is measurable.
 */
function useMirroredSideOffset(
  rowEl: HTMLElement | null,
  contentEl: HTMLElement | null,
  enabled: boolean,
): number | undefined {
  const [offset, setOffset] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!enabled || !rowEl) {
      setOffset(undefined);
      return;
    }
    const parent = rowEl.closest("[data-radix-menu-content]");
    if (!(parent instanceof HTMLElement)) return;
    const rowRect = rowEl.getBoundingClientRect();
    const childWidth = (contentEl ?? parent).getBoundingClientRect().width;
    // The extra CASCADE_GAP_PX mirrors the rightward gap, so leftward levels
    // keep the same seam instead of butting the parent panel.
    setOffset(rowRect.left - childWidth - rowRect.right - CASCADE_GAP_PX);
  }, [contentEl, enabled, rowEl]);
  return offset;
}

/* -------------------------------------------------------------------------- */
/* Dropdown host (Radix DropdownMenuSub)                                      */
/* -------------------------------------------------------------------------- */

/**
 * Submenu level whose trigger row is itself clickable. Click/Enter runs
 * `onCommit` (when set); hover/ArrowRight expands. Handles leftward cascade
 * continuation for its content.
 */
export function CascadeSub({
  row,
  disabled,
  onCommit,
  children,
}: {
  row: ReactNode;
  disabled?: boolean;
  /** Commit for click/Enter on the row itself; omit to only expand. */
  onCommit?: () => void;
  children: ReactNode;
}) {
  const inheritedSide = useContext(CascadeSideContext);
  const [rowEl, setRowEl] = useState<HTMLElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  const measuredSide = useMeasuredSide(contentEl);
  const forcedLeft = inheritedSide === "left";
  const mirroredOffset = useMirroredSideOffset(rowEl, contentEl, forcedLeft);
  // A forced level renders with placement "right" (offset-mirrored), so its
  // data-side is meaningless — propagate "left" directly.
  const sideForChildren: CascadeSide = forcedLeft ? "left" : measuredSide;

  // Forced-left levels own their open state. Radix builds its pointer-grace
  // polygon from the content's DECLARED side, and a forced level declares
  // "right" while rendering left — the safe corridor points away from the
  // actual panel, so heading toward it exits the polygon and Radix snaps the
  // sub shut (the intermittent dead spot that made deep leaves unreachable
  // near screen edges). Hover-intent timers replace the polygon — the same
  // mechanism the command-menu host below uses — while Radix keeps keyboard
  // opens; ArrowLeft/Escape close explicitly on the content.
  const [forcedOpen, setForcedOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const cancelClose = useCallback(
    () => window.clearTimeout(closeTimer.current),
    [],
  );
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setForcedOpen(false), 160);
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);
  const holdOpen = useCallback(() => {
    cancelClose();
    setForcedOpen(true);
  }, [cancelClose]);

  return (
    <DropdownMenuSub
      {...(forcedLeft
        ? {
            open: forcedOpen,
            onOpenChange: (next: boolean) => {
              // Honor opens (Radix hover intent, ArrowRight). Closes are
              // ignored — Radix only requests them from the misdirected
              // grace polygon; the timers own closing.
              if (next && !disabled) holdOpen();
            },
          }
        : {})}
    >
      <DropdownMenuSubTrigger
        ref={setRowEl}
        disabled={disabled}
        onPointerEnter={forcedLeft && !disabled ? holdOpen : undefined}
        onPointerLeave={forcedLeft ? scheduleClose : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!onCommit) return;
          // Suppress Radix's open-on-click: a direct click commits to this
          // container; hovering (or ArrowRight) expands it.
          e.preventDefault();
          onCommit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onCommit) {
            e.preventDefault();
            onCommit();
          }
        }}
      >
        {row}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        ref={setContentEl}
        onClick={(e) => e.stopPropagation()}
        onPointerEnter={forcedLeft ? cancelClose : undefined}
        onPointerLeave={forcedLeft ? scheduleClose : undefined}
        onKeyDown={
          forcedLeft
            ? (e) => {
                if (e.key === "ArrowLeft" || e.key === "Escape") {
                  setForcedOpen(false);
                }
              }
            : undefined
        }
        className="max-w-72 max-h-[min(60vh,420px)] overflow-y-auto"
        // Same seam as the command-menu popover variant (its PopoverContent
        // uses sideOffset={CASCADE_GAP_PX} from the row): the trigger row sits
        // inset by the panel's p-1, so Radix's default 0 would start the child
        // INSIDE the parent panel — the overlap this offset exists to remove.
        {...(forcedLeft && mirroredOffset !== undefined
          ? { sideOffset: mirroredOffset, avoidCollisions: false }
          : { sideOffset: CASCADE_GAP_PX })}
      >
        <CascadeSideContext.Provider value={sideForChildren}>
          {children}
        </CascadeSideContext.Provider>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export interface DestinationMenuSubProps {
  submenu: ObjectActionDestinationSubmenu;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
}

export function DestinationDropdownMenuSub({
  submenu,
  label,
  icon,
  disabled,
}: DestinationMenuSubProps) {
  return (
    <CascadeSub
      disabled={disabled}
      row={
        <>
          {icon}
          {label}
        </>
      }
    >
      <DropdownWorkspaceList submenu={submenu} />
    </CascadeSub>
  );
}

function DropdownWorkspaceList({
  submenu,
}: {
  submenu: ObjectActionDestinationSubmenu;
}) {
  const workspaces = useSortedWorkspaces(submenu);

  if (workspaces.length === 0) {
    return <EmptyWorkspacesRow />;
  }

  return (
    <>
      {workspaces.map((ws) => (
        <DropdownWorkspaceRow key={ws.id} workspace={ws} submenu={submenu} />
      ))}
    </>
  );
}

function DropdownWorkspaceRow({
  workspace,
  submenu,
}: {
  workspace: Workspace;
  submenu: ObjectActionDestinationSubmenu;
}) {
  // Fetched when the workspace list shows (not on row hover) so the row knows
  // whether to render as a plain commit item or an expandable submenu.
  const { tree, isLoading } = useWorkspaceFolderTree(workspace.id, submenu);
  const commitAndClose = useCommitAndClose(submenu);
  const label = workspaceLabel(workspace);
  const Icon = workspace.is_private ? User : Layers;
  const rootIsCurrent =
    isCurrentLocation(submenu, workspace.id, "") ||
    isNotWritable(submenu, workspace.id);
  const destination: TransferDestination = {
    workspaceId: workspace.id,
    folderPath: "",
    label,
  };
  const row = (
    <>
      <Icon className="text-foreground/60" />
      <span className="min-w-0 truncate">{label}</span>
    </>
  );

  if (isLoading || tree.length === 0) {
    return (
      <DropdownMenuItem
        disabled={rootIsCurrent}
        onClick={(e) => {
          e.stopPropagation();
          commitAndClose(destination);
        }}
      >
        {row}
      </DropdownMenuItem>
    );
  }

  return (
    <CascadeSub
      row={row}
      onCommit={rootIsCurrent ? undefined : () => commitAndClose(destination)}
    >
      {tree.map((node) => (
        <DropdownFolderRow
          key={node.path}
          node={node}
          workspace={workspace}
          parentLabel={label}
          submenu={submenu}
        />
      ))}
    </CascadeSub>
  );
}

function DropdownFolderRow({
  node,
  workspace,
  parentLabel,
  submenu,
}: {
  node: FolderNode;
  workspace: Workspace;
  parentLabel: string;
  submenu: ObjectActionDestinationSubmenu;
}) {
  const commitAndClose = useCommitAndClose(submenu);
  const forbidden = isForbiddenFolder(submenu, workspace.id, node.path);
  const isCurrent = isCurrentLocation(submenu, workspace.id, node.path);
  const label = `${parentLabel} / ${node.name}`;
  const destination = folderDestination(submenu, workspace.id, node, label);
  const row = (
    <>
      <Folder />
      <span className="min-w-0 truncate">{node.name}</span>
    </>
  );

  if (node.children.length === 0) {
    return (
      <DropdownMenuItem
        disabled={forbidden || isCurrent}
        onClick={(e) => {
          e.stopPropagation();
          commitAndClose(destination);
        }}
      >
        {row}
      </DropdownMenuItem>
    );
  }

  return (
    <CascadeSub
      row={row}
      disabled={forbidden}
      onCommit={
        forbidden || isCurrent ? undefined : () => commitAndClose(destination)
      }
    >
      {node.children.map((child) => (
        <DropdownFolderRow
          key={child.path}
          node={child}
          workspace={workspace}
          parentLabel={label}
          submenu={submenu}
        />
      ))}
    </CascadeSub>
  );
}

/* -------------------------------------------------------------------------- */
/* Command host (cmdk rows + non-portaled popovers)                           */
/* -------------------------------------------------------------------------- */

/**
 * cmdk row that opens a nested, non-portaled popover on hover — the same
 * mechanism as the download-formats submenu, with an added open delay so
 * sweeping the pointer across rows doesn't flicker submenus open. Non-portaled
 * keeps the nested content inside the outer popover's DOM, so the command
 * menu's `onInteractOutside` close doesn't fire when clicking into a level.
 * Popovers accept `side`, so leftward cascade continuation is native here:
 * each level prefers its parent's rendered side and measures its own.
 */
export function CommandCascadeSub({
  value,
  row,
  disabled,
  onCommit,
  children,
}: {
  value: string;
  row: ReactNode;
  disabled?: boolean;
  /** Commit for click/Enter on the row itself; omit to only expand. */
  onCommit?: () => void;
  children: ReactNode;
}) {
  const inheritedSide = useContext(CascadeSideContext);
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  const measuredSide = useMeasuredSide(contentEl);
  const [open, setOpen] = useState(false);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);

  const clearTimers = useCallback(() => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const openSoon = () => {
    clearTimers();
    if (disabled) return;
    openTimer.current = window.setTimeout(() => setOpen(true), 150);
  };
  const openNow = () => {
    clearTimers();
    if (!disabled) setOpen(true);
  };
  const closeSoon = () => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <CommandItem
          value={value}
          disabled={disabled}
          onMouseEnter={openSoon}
          onMouseLeave={closeSoon}
          onSelect={() => {
            if (onCommit) onCommit();
            else openNow();
          }}
        >
          {row}
        </CommandItem>
      </PopoverAnchor>
      <PopoverContent
        ref={setContentEl}
        align="start"
        disablePortal
        side={inheritedSide}
        sideOffset={CASCADE_GAP_PX}
        className="w-auto min-w-32 max-w-72 p-1"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <CascadeSideContext.Provider value={measuredSide}>
          {children}
        </CascadeSideContext.Provider>
      </PopoverContent>
    </Popover>
  );
}

export function DestinationCommandMenuSub({
  submenu,
  label,
  icon,
  disabled,
}: DestinationMenuSubProps) {
  return (
    <CommandCascadeSub
      value="destination-submenu-root"
      disabled={disabled}
      row={
        <>
          {icon}
          {label}
          <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
        </>
      }
    >
      <CommandWorkspaceList submenu={submenu} />
    </CommandCascadeSub>
  );
}

function CommandWorkspaceList({
  submenu,
}: {
  submenu: ObjectActionDestinationSubmenu;
}) {
  const workspaces = useSortedWorkspaces(submenu);

  if (workspaces.length === 0) {
    return <EmptyWorkspacesRow />;
  }

  return (
    <>
      {workspaces.map((ws) => (
        <CommandWorkspaceRow key={ws.id} workspace={ws} submenu={submenu} />
      ))}
    </>
  );
}

function CommandWorkspaceRow({
  workspace,
  submenu,
}: {
  workspace: Workspace;
  submenu: ObjectActionDestinationSubmenu;
}) {
  const { tree, isLoading } = useWorkspaceFolderTree(workspace.id, submenu);
  const commitAndClose = useCommitAndClose(submenu);
  const label = workspaceLabel(workspace);
  const Icon = workspace.is_private ? User : Layers;
  const rootIsCurrent =
    isCurrentLocation(submenu, workspace.id, "") ||
    isNotWritable(submenu, workspace.id);
  const destination: TransferDestination = {
    workspaceId: workspace.id,
    folderPath: "",
    label,
  };

  if (isLoading || tree.length === 0) {
    return (
      <CommandItem
        value={`destination-ws-${workspace.id}`}
        disabled={rootIsCurrent}
        onSelect={() => commitAndClose(destination)}
      >
        <Icon className="text-foreground/60" />
        <span className="min-w-0 truncate">{label}</span>
      </CommandItem>
    );
  }

  return (
    <CommandCascadeSub
      value={`destination-ws-${workspace.id}`}
      row={
        <>
          <Icon className="text-foreground/60" />
          <span className="min-w-0 truncate">{label}</span>
          <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
        </>
      }
      onCommit={rootIsCurrent ? undefined : () => commitAndClose(destination)}
    >
      {tree.map((node) => (
        <CommandFolderRow
          key={node.path}
          node={node}
          workspace={workspace}
          parentLabel={label}
          submenu={submenu}
        />
      ))}
    </CommandCascadeSub>
  );
}

function CommandFolderRow({
  node,
  workspace,
  parentLabel,
  submenu,
}: {
  node: FolderNode;
  workspace: Workspace;
  parentLabel: string;
  submenu: ObjectActionDestinationSubmenu;
}) {
  const commitAndClose = useCommitAndClose(submenu);
  const forbidden = isForbiddenFolder(submenu, workspace.id, node.path);
  const isCurrent = isCurrentLocation(submenu, workspace.id, node.path);
  const label = `${parentLabel} / ${node.name}`;
  const destination = folderDestination(submenu, workspace.id, node, label);

  if (node.children.length === 0) {
    return (
      <CommandItem
        value={`destination-${workspace.id}-${node.path}`}
        disabled={forbidden || isCurrent}
        onSelect={() => commitAndClose(destination)}
      >
        <Folder />
        <span className="min-w-0 truncate">{node.name}</span>
      </CommandItem>
    );
  }

  return (
    <CommandCascadeSub
      value={`destination-${workspace.id}-${node.path}`}
      disabled={forbidden}
      row={
        <>
          <Folder />
          <span className="min-w-0 truncate">{node.name}</span>
          <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
        </>
      }
      onCommit={
        forbidden || isCurrent ? undefined : () => commitAndClose(destination)
      }
    >
      {node.children.map((child) => (
        <CommandFolderRow
          key={child.path}
          node={child}
          workspace={workspace}
          parentLabel={label}
          submenu={submenu}
        />
      ))}
    </CommandCascadeSub>
  );
}

/* -------------------------------------------------------------------------- */
/* Standalone wrapper for hand-written (non-registry) dropdown menus          */
/* -------------------------------------------------------------------------- */
